import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, History } from 'lucide-react';

import { listHistoryRange } from '@/db/history';
import type { HistoryRow } from '@/db/schema';
import { subscribeAccountDataRestored } from '@/features/account/accountDataEvents';
import { getSource } from '@/sources/registry';
import { cn } from '@/lib/utils';

export interface ContinueReadingProps {
  /** How many tiles to show in the carousel. */
  limit?: number;
  /** If true, load more rows as the horizontal rail nears the end. */
  infinite?: boolean;
  className?: string;
}

/**
 * Horizontal carousel of recently-read galleries with a small progress bar.
 *
 * Renders nothing while loading and nothing when empty — the Home page can
 * include this unconditionally and it stays out of the way for new users.
 */
export function ContinueReading({ limit = 10, infinite = false, className }: ContinueReadingProps) {
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [rawOffset, setRawOffset] = useState(0);
  const [reloadTick, setReloadTick] = useState(0);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(
    () => subscribeAccountDataRestored(() => setReloadTick((tick) => tick + 1)),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listHistoryRange(0, limit)
      .then((result) => {
        if (cancelled) return;
        setRows(dedupeHistoryByComic(result.rows));
        setRawOffset(result.rows.length);
        setHasMore(result.hasMore);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [limit, reloadTick]);

  const loadMore = useCallback(async () => {
    if (!infinite || loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const result = await listHistoryRange(rawOffset, limit);
      setRows((cur) => dedupeHistoryByComic([...cur, ...result.rows]));
      setRawOffset((current) => current + result.rows.length);
      setHasMore(result.hasMore);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, infinite, limit, loadingMore, rawOffset]);

  useEffect(() => {
    if (!infinite || !hasMore) return;
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void loadMore();
        }
      },
      { rootMargin: '0px 240px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, infinite, loadMore]);

  if (loading || rows.length === 0) return null;

  return (
    <section className={cn('flex flex-col gap-3', className)}>
      <div className="flex items-end justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold tracking-tight">
          <History className="h-4 w-4" />
          Continue reading
        </h2>
        <Link
          to="/library/history"
          className="flex items-center gap-1 text-xs text-muted-foreground hover:underline"
        >
          See all
          <ChevronRight className="h-3 w-3" />
        </Link>
      </div>

      <div
        className={cn(
          'flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2',
          // Hide the scrollbar visually but keep it functional.
          '[scrollbar-width:thin]',
        )}
      >
        {rows.map((row) => {
          const sourceName = getSource(row.source)?.name ?? row.source;
          const pct =
            row.totalPages > 0
              ? Math.max(0, Math.min(100, Math.round((row.lastPage / row.totalPages) * 100)))
              : 0;
          return (
            <Link
              key={row.key}
              to={historyReadPath(row)}
              className="group flex w-36 shrink-0 snap-start flex-col gap-1.5"
              title={`${row.titleSnapshot} — page ${row.lastPage} of ${row.totalPages}`}
            >
              <div className="relative aspect-[3/4] overflow-hidden rounded bg-muted">
                {row.coverUrl && (
                  <img
                    src={row.coverUrl}
                    alt=""
                    referrerPolicy="no-referrer"
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform group-hover:scale-[1.02]"
                  />
                )}
                <div className="absolute inset-x-0 bottom-0 h-1 bg-black/50">
                  <div
                    className="h-full bg-primary"
                    style={{ width: `${pct}%` }}
                    aria-label={`${pct}% read`}
                  />
                </div>
              </div>
              <h3 className="line-clamp-2 text-xs font-medium leading-tight">{row.titleSnapshot}</h3>
              {row.readUnitTitle && (
                <p className="line-clamp-1 text-[10px] text-muted-foreground">
                  {row.readGroupTitle ? `${row.readGroupTitle} · ` : ''}
                  {row.readUnitTitle}
                </p>
              )}
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                {sourceName} · p{row.lastPage}/{row.totalPages}
              </p>
            </Link>
          );
        })}
        {infinite && hasMore && <div ref={sentinelRef} className="w-px shrink-0" aria-hidden />}
        {loadingMore && (
          <div className="flex w-24 shrink-0 items-center justify-center text-xs text-muted-foreground">
            Loading…
          </div>
        )}
      </div>
    </section>
  );
}

function historyReadPath(row: HistoryRow): string {
  const params = new URLSearchParams();
  if (row.readUnitId) params.set('unit', row.readUnitId);
  const query = params.toString();
  return `/source/${row.source}/comic/${encodeURIComponent(row.comicId)}${query ? `?${query}` : ''}`;
}

function dedupeHistoryByComic(rows: HistoryRow[]): HistoryRow[] {
  const seen = new Set<string>();
  return rows.filter((row) => {
    const key = `${row.source}|${row.comicId}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
